/**
 * Het paneel met de toelichtingen bij de arbeidsvoorwaarden.
 *
 * Elke voorwaardenkaart met een toelichting heeft linksonder een plusje. Dat opent het
 * paneel rechts in beeld op die toelichting; daarbinnen blader je met de pijltjes, de
 * bolletjes of door op een ander plusje te klikken langs alle toelichtingen van dat blok.
 *
 * Het bladeren zelf zit in CSS: de toelichtingen liggen op elkaar in dezelfde rasterhoek
 * en dit script zet alleen data-benefit-status. Het gaat in twee tellen -- de nieuwe kaart
 * eerst zonder overgang opzij zetten, dan beide in beweging -- zodat de inhoud de kant op
 * schuift waar je heen bladert. De maten en de duur staan in common/benefit-modal.scss;
 * DUUR hieronder moet gelijk lopen met de transition daar.
 *
 * Per sectie een eigen paneel: staan er twee van deze blokken op een pagina, dan hoort
 * het plusje van de een niet het paneel van de ander te openen.
 */

const DUUR = 600;

/**
 * Het paneel legt beslag op de rechteronderhoek, en daar zweeft de contactwidget al.
 * In plaats van die widget hiervandaan aan te sturen -- dan moet dit script weten wat er
 * verder nog in beeld hangt -- meldt het paneel alleen dat het openstaat. Wie in de weg
 * zit luistert mee; zie resources/scripts/contact-widget.js.
 *
 * @param {boolean} open
 */
function meld(open) {
  document.dispatchEvent(new CustomEvent('overlay:toggle', { detail: { open } }));
}

export default function initVacancyBenefits() {
  const secties = [...document.querySelectorAll('.vacancy-benefits')];

  secties.forEach((sectie) => {
    const modal = sectie.querySelector('[data-benefit-modal]');
    const knoppen = [...sectie.querySelectorAll('[data-benefit-open]')];

    if (!modal || !knoppen.length) {
      return;
    }

    const items = [...modal.querySelectorAll('[data-benefit-item]')];
    const bolletjes = [...modal.querySelectorAll('.benefit-modal__dot')];

    if (!items.length) {
      return;
    }

    let actief = 0;
    let isOpen = false;
    let opruimen;

    function markeer() {
      // De knop is de kaart zelf, dus de status erop stuurt zowel het plusje als de rand
      // om de kaart heen aan; zie common/vacancy-benefits.scss.
      knoppen.forEach((knop) => {
        const aan = isOpen && Number(knop.dataset.benefitOpen) === actief;

        knop.dataset.benefitStatus = aan ? 'active' : 'not-active';
        knop.setAttribute('aria-expanded', String(aan));
      });

      bolletjes.forEach((bol, i) => {
        bol.dataset.benefitStatus = i === actief ? 'active' : 'not-active';
        bol.setAttribute('aria-current', String(i === actief));
      });
    }

    /**
     * @param {number} index
     * @param {'next'|'prev'} richting
     */
    function toon(index, richting) {
      if (index === actief) {
        return;
      }

      window.clearTimeout(opruimen);

      const huidig = items[actief];
      const volgend = items[index];

      huidig.dataset.benefitStatus = richting === 'next' ? 'transition-out-left' : 'transition-out-right';
      volgend.dataset.benefitStatus = richting === 'next' ? 'transition-in-right' : 'transition-in-left';

      // De nieuwe kaart staat nu opzij zonder overgang. Even de opmaak laten doorrekenen,
      // anders ziet de browser alleen de eindstand en schuift er niets.
      void volgend.offsetHeight;

      volgend.dataset.benefitStatus = 'active';
      actief = index;
      markeer();

      opruimen = window.setTimeout(() => {
        huidig.dataset.benefitStatus = 'not-active';
      }, DUUR);
    }

    function open(index) {
      window.clearTimeout(opruimen);

      items.forEach((item, i) => {
        item.dataset.benefitStatus = i === index ? 'active' : 'not-active';
      });

      actief = index;
      isOpen = true;

      modal.dataset.benefitModalStatus = 'active';
      modal.setAttribute('aria-hidden', 'false');

      meld(true);
      markeer();
    }

    function sluit() {
      if (!isOpen) {
        return;
      }

      isOpen = false;

      modal.dataset.benefitModalStatus = 'not-active';
      modal.setAttribute('aria-hidden', 'true');

      meld(false);
      markeer();
    }

    knoppen.forEach((knop) => {
      knop.addEventListener('click', () => {
        const index = Number(knop.dataset.benefitOpen);

        if (isOpen && index === actief) {
          sluit();

          return;
        }

        if (isOpen) {
          toon(index, index > actief ? 'next' : 'prev');

          return;
        }

        open(index);
      });
    });

    modal.addEventListener('click', (e) => {
      const knop = e.target.closest('[data-benefit-control]');

      if (!knop) {
        return;
      }

      const actie = knop.dataset.benefitControl;

      if (actie === 'close') {
        sluit();

        return;
      }

      if (actie === 'next') {
        toon((actief + 1) % items.length, 'next');

        return;
      }

      if (actie === 'prev') {
        toon((actief - 1 + items.length) % items.length, 'prev');

        return;
      }

      const index = Number(actie);

      if (Number.isInteger(index) && index !== actief) {
        toon(index, index > actief ? 'next' : 'prev');
      }
    });

    // Een klik ergens anders op de pagina sluit het paneel. De plusjes zelf niet: die
    // doen hierboven al hun eigen ding, en zouden anders openen en meteen weer sluiten.
    document.addEventListener('click', (e) => {
      if (isOpen && !modal.contains(e.target) && !e.target.closest('[data-benefit-open]')) {
        sluit();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        sluit();
      }
    });
  });
}
